/**
 * liveReportsViewModel — pure `(reduxState) → page props` for the parent Live Reports screen.
 *
 * No hooks, no navigation, no API calls. The Screen does:
 *   `const vm = useSelector(liveReportsViewModel)`
 *
 * Page cursor / `hasMore` live in `useLiveReportsData` (local state from `meta`).
 */
import { toUILiveSessionReports } from "../../../adapters/parent/LiveReports/liveReportListAdapter"
import { type UILiveSessionReport } from "../../../ui/types"

export type LiveReportsViewModel = {
    reports: UILiveSessionReport[]
    /** First-load spinner (no rows yet). */
    loading: boolean
    /** Soft refresh while rows already shown (page 1). */
    refreshing: boolean
    /** Next-page footer spinner. */
    loadingMore: boolean
    /** Blocking error — only when there is nothing to show; drives the page-level banner. */
    error: unknown | null
    /** Next-page error while rows are shown — drives the list's footer banner. */
    loadMoreError: string | null
}

function errorText(error: unknown): string {
    if (typeof error === "string" && error.trim()) return error
    if (typeof error === "object" && error !== null && "message" in error) {
        const message = (error as { message?: unknown }).message
        if (typeof message === "string" && message.trim()) return message
    }
    return "Impossible de charger la suite des rapports"
}

/** Redux root is still untyped in this app — keep the VM boundary explicit. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function liveReportsViewModel(state: any): LiveReportsViewModel {
    return toLiveReportsViewModel(
        state?.data?.liveReports?.liveReports ?? [],
        state?.data?.staticData?.school_subjects ?? [],
        state?.api?.getStudentLiveReports
    )
}

/**
 * The list's page props from its rows, the subject catalog and the `getStudentLiveReports` slice — pure,
 * shared with the tutor's Reports tab, which passes the rows of ONE student.
 */
export function toLiveReportsViewModel(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    raw: any[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    subjects: any[] | null | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    req: any
): LiveReportsViewModel {
    const requesting = !!req?.request
    const error = req?.error ?? null
    const page = Number(req?.urlParams?.page ?? 1)
    const hasRows = raw.length > 0

    return {
        reports: toUILiveSessionReports(raw, subjects),
        loading: requesting && !hasRows,
        refreshing: requesting && hasRows && page <= 1,
        loadingMore: requesting && hasRows && page > 1,
        error: hasRows ? null : error,
        loadMoreError: hasRows && error != null ? errorText(error) : null,
    }
}
